import { classes } from 'html-classes';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';
import {
  type ChangeEvent,
  type JSX,
  type KeyboardEvent,
  useEffect,
  useMemo,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useSearchParams } from 'react-router-dom';

import { company } from '~/company/Company';
import CategoryBlock, {
  CategoryDisplayType,
} from '~/components/CategoryBlocks/CategoryBlock';
import { SommelierButton } from '~/components/HeaderV3/components/SearchBar/components/SommelierButton';
import { useGlobal } from '~/hooks/useGlobal';
import { useStores } from '~/hooks/useStore';
import { useSearchRecommendations } from '~/pages/SearchResults/hooks/useSearchRecommendations';
import { SearchLoader } from '~/pages/SearchResults/SearchLoader';
import { SearchRecommendations } from '~/pages/SearchResults/SearchRecommendations';
import { AnalyticsEvent, analyticsService } from '~/services/analytics';
import { userStore } from '~/stores';
import type { Category } from '~/stores/CategoriesStore';
import { searchStore } from '~/stores/SearchStore';
import { copyWith } from '~/utils/copy';
import resetScroll from '~/utils/resetScroll';
import { sortProductsByStock } from '~/utils/sortProductsByStock';

import Icon from '../../components/Icon/Icon';
import ProductCard from '../../components/ProductCard/ProductCard';
import { SearchResultsV3 } from '../../pages/SearchResultsV3';

import styles from './SearchResults.module.scss';

const Header = observer(() => {
  const [searchParams, setSearchParams] = useSearchParams();

  const setQuery = (str?: string) => {
    if (str?.trim()) {
      searchParams.set('q', str);
    } else {
      searchParams.delete('q');
    }
    setSearchParams(searchParams, { replace: true });
  };

  const handleKeyUp = (e: KeyboardEvent<HTMLInputElement>): void => {
    const value = (e.target as HTMLInputElement).value;

    if (e.code !== 'Enter' || !value) {
      return;
    }

    e.preventDefault();
    setQuery(value);
    searchStore.paginationRequest.debounceLoadPage(1, value);
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>): void => {
    searchStore.setQuery(e.target.value);
    setQuery(e.target.value);
    searchStore.paginationRequest.debounceLoadPage(1, e.target.value);
  };

  const handleReset = () => {
    searchStore.reset();
    setQuery();
    resetScroll();
  };

  useEffect(() => {
    const currentQueryString = searchParams.get('q');

    if (!searchStore.queryString && currentQueryString) {
      searchStore.setQuery(currentQueryString ?? '');
      searchStore.paginationRequest.debounceLoadPage(1, currentQueryString);
    } else if (searchStore.queryString) {
      searchParams.set('q', searchStore.queryString);
      setSearchParams(searchParams, { replace: true });
    }
  }, []);

  return (
    <div className={styles.mobileHeaderSearch}>
      <Link to="/">
        <Icon type="arrow" size={24} className="_mirror-x" />
      </Link>
      <div className="header-search">
        {searchStore.paginationRequest.isLoading ? (
          <div className="spinner header-search__icon" />
        ) : (
          <Icon className="header-search__icon" type="search" size={24} />
        )}

        <input
          id="header-search"
          type="text"
          value={searchStore.queryString}
          autoFocus
          className="header-search__input"
          placeholder={t('phrases:hiThere', {
            name: userStore.personalData.firstName || 'there',
          })}
          onKeyUp={handleKeyUp}
          onChange={handleInputChange}
        />
        <button
          className="button _no-padding"
          style={{ visibility: searchStore.queryString ? 'visible' : 'hidden' }}
          onClick={handleReset}
        >
          <Icon type="close" />
        </button>
      </div>
    </div>
  );
});

const SearchResults = observer((): JSX.Element => {
  const { t } = useTranslation();
  const { isMobile } = useGlobal();
  const { state } = useLocation();
  const [recommendations, selectRecommendations] = useSearchRecommendations();
  const { homeCategories } = useStores();

  useEffect(() => {
    analyticsService.track(AnalyticsEvent.CATALOG_SEARCH_VISITED, {
      source: state?.source || 'direct',
    });
    analyticsService.track(AnalyticsEvent.CATALOG_SEARCH_VISITED_BI, {
      source: state?.source || 'direct',
    });
  }, []);

  const getCategoryByName = (name: string) => {
    const category = homeCategories.list.find((category) =>
      category.name.toLowerCase().includes(name),
    );

    return category
      ? copyWith(category, { previewText: '', backgroundColor: null })
      : null;
  };

  const categories = [
    getCategoryByName('recently viewed'),
    getCategoryByName('recommended'),
  ].filter(Boolean) as Category[];

  const sortedSearchResults = useMemo(() => {
    if (!searchStore.result) {
      return null;
    }
    return sortProductsByStock(searchStore.result);
  }, [searchStore.result]);

  return (
    <div className={classes(['page-catalog', styles.root])}>
      <div className="content-layout">
        <div className="two-columns">
          <div className="two-columns__content">
            <div>
              <div className={styles.sticky}>
                <div className="block-title-wrap">
                  {isMobile ? (
                    <Header />
                  ) : (
                    <div className="container-flex _v-baseline">
                      <h1
                        className={classes(['block-title', styles.pageTitle])}
                      >
                        {t('translation:searchResult')}
                      </h1>
                    </div>
                  )}
                </div>
              </div>
              {!(sortedSearchResults?.length ?? 0) &&
              searchStore.paginationRequest.isLoading ? (
                <SearchLoader />
              ) : (sortedSearchResults?.length ?? 0) !== 0 ? (
                <>
                  <div data-company={company.name} className="block-products">
                    {(sortedSearchResults ?? []).map((product) => (
                      <ProductCard
                        key={product.id}
                        product={product}
                        variant={company.productCardVariantL}
                      />
                    ))}
                  </div>
                </>
              ) : !(sortedSearchResults?.length ?? 0) &&
                searchStore.queryString.length > 0 ? (
                <div
                  className={styles.nothingFound}
                  data-company={company.name}
                >
                  <img
                    src={company.images.search.empty}
                    className={styles.emptyImage}
                    alt=""
                  />
                  <h3>
                    {t('searchNotFound', { query: searchStore.queryString })}
                  </h3>
                  <p>{t('searchTryAnother')}</p>
                </div>
              ) : isMobile ? (
                <>
                  <div>
                    <SearchRecommendations
                      data={recommendations}
                      onSelect={selectRecommendations}
                    />
                  </div>
                  <div className={styles.categories}>
                    {categories.map((category) => (
                      <CategoryBlock
                        className={
                          category.displayType !==
                          CategoryDisplayType.CATEGORIES_CAROUSEL_SLIDER_TWO
                            ? 'content-layout'
                            : undefined
                        }
                        category={category}
                        key={category.id}
                      />
                    ))}
                  </div>
                  {isMobile && company.isSommelierEnabled && (
                    <div className={styles.section}>
                      <div className={styles.banner}>
                        <div className={styles.bannerContent}>
                          <div className={styles.bannerText}>
                            <p>{t('header:imGenie')}</p>
                            <p>{t('header:personalAISommelier')}</p>
                          </div>
                          <SommelierButton />
                        </div>
                      </div>
                    </div>
                  )}
                </>
              ) : null}
            </div>
          </div>
        </div>
      </div>
      {(sortedSearchResults?.length ?? 0) > 0 &&
        searchStore.paginationRequest.canLoadMore && (
          <SearchLoader
            onLoadMore={() =>
              searchStore.paginationRequest.loadMore(searchStore.queryString)
            }
          />
        )}
    </div>
  );
});

export default company.match({
  citydrinks: company.newDesignSystemEnabled ? SearchResultsV3 : SearchResults,
  v3: SearchResultsV3,
  default: SearchResults,
});
